import React, { useEffect, useState } from 'react';
import { InputField } from '../../../atoms/input-field';
import { SelectBox } from '../../../atoms/select-box';
import { TextAreaField } from '../../../atoms/text-area-field';
import { FormField, InputValue, isEmail } from '../donate-form-utils';
import styles from '../DonateFormApp.scss';

interface DonateFormInputProps {
    field: FormField;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    value: InputValue;
}

const DonateFormInput = ({ field, handleInputChange, value }: DonateFormInputProps) => {
    const [errorMessage, setErrorMessage] = useState<string>();
    const { name, fieldInputProps, type, config } = field;
    const id = name.toLowerCase().split(' ').join('-');

    const checkForErrors = (newValue: InputValue) => {
        const {required, min, max, type: inputType} = fieldInputProps;

        if (required !== undefined && !newValue) {
            setErrorMessage('Field is required');

            return;
        }

        if (min !== undefined && newValue && Number(newValue) < Number(min)) {
            setErrorMessage(`${name} must be at least ${min}`);

            return;
        }

        if (max !== undefined && newValue && Number(newValue) > Number(max)) {
            setErrorMessage(`${name} must be lower than ${max}`);

            return;
        }

        if (inputType === 'email' && !isEmail(newValue as string)) {
            setErrorMessage('Please enter a valid email address');

            return;
        }

        return;
    }

    const [isFirstRender, setIsFirstRender] = useState(true);

    useEffect(() => {
        // needed so we don't render all errors at first
        if (isFirstRender) {
            setIsFirstRender(false);

            return;
        }

        setErrorMessage(undefined);
        checkForErrors(value);
    }, [value]);

    if (type === 'select') {
        return (
            <SelectBox
                appearance="light"
                options={
                    config.selectOptions!.map((option) => ({
                        name: option,
                        value: option,
                    }))
                }
                {...fieldInputProps}
                id={id}
                label={name}
                onChange={handleInputChange}
                value={value}
            />
        );
    }

    const onBlur = () => {
        checkForErrors(value);
    }

    const fieldProps = {
        ...fieldInputProps,
        id,
        label: name,
        onChange: handleInputChange,
        onBlur,
        isError: Boolean(errorMessage),
        errorMessage: errorMessage,
        value,
    }

    if (field.name === 'Amount') {
        const { givingMultiplier } = config;

        return (
            <InputField
                {...fieldProps}
                appearance="light"
                prefix="$"
                suffixSlot={givingMultiplier && givingMultiplier > 1 ? (
                    <div className={styles['donate-form-input__matched-giving']}>
                        Matched x{givingMultiplier} = ${value ? Number(value) * givingMultiplier : 0}
                    </div>
                ) : null}
            />
        );
    }

    if (field.type === 'input') {
        return (
            <InputField
                appearance="light"
                {...fieldProps}
            />
        )
    }

    if (field.type === 'textarea') {
        return (
            <TextAreaField
                appearance="light"
                {...fieldProps}
            />
        )
    }

    return <></>;
}

export default DonateFormInput